import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api, formatKES } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Users, Briefcase, UserCheck, TrendingUp, Plus, Trash2, Star, Pencil, ShieldAlert, FileSignature, Scale, DollarSign, BookOpen, Save, ShieldCheck, CheckCircle2, XCircle, FileText, Eye, Send, MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import InlineChat from "../components/InlineChat";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [fees, setFees] = useState({ one_time_hire_fee: 500, payroll_commission_pct: 5 });
  const [editTier, setEditTier] = useState(null);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [placeData, setPlaceData] = useState({ customer_id: "", employee_id: "", role: "", salary: 0, start_date: "" });
  const [industryRates, setIndustryRates] = useState([]);
  const [editRate, setEditRate] = useState(null);
  const [houseRules, setHouseRules] = useState({ content: "", version: "" });
  const [contracts, setContracts] = useState([]);
  const [reports, setReports] = useState([]);
  const [contractOpen, setContractOpen] = useState(false);
  const [contractForm, setContractForm] = useState({
    customer_id: "", employee_id: "", role_title: "", salary_agreed: 0,
    pay_frequency: "monthly", start_date: "", notice_period_days: 14,
    probation_days: 30, additional_terms: "", working_hours: "8:00 AM – 5:00 PM, Mon–Sat",
  });
  const [reportEdit, setReportEdit] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [docReview, setDocReview] = useState(null);
  const [prospectDocs, setProspectDocs] = useState({ open: false, employee: null, docs: [] });
  const [requestDocs, setRequestDocs] = useState({ open: false, employee_id: null, requested_kinds: [], note: "" });
  const [contractEdit, setContractEdit] = useState(null);

  const loadAll = async () => {
    const [s, c, e, p, t, r, tr, f, ir, hr, ct, rp, dc] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/customers"),
      api.get("/admin/employees"),
      api.get("/admin/placements"),
      api.get("/tickets"),
      api.get("/admin/reviews"),
      api.get("/tiers"),
      api.get("/admin/fee-config"),
      api.get("/industry-rates"),
      api.get("/house-rules"),
      api.get("/admin/contracts"),
      api.get("/reports"),
      api.get("/admin/documents"),
    ]);
    setStats(s.data);
    setCustomers(c.data);
    setEmployees(e.data);
    setPlacements(p.data);
    setTickets(t.data);
    setReviews(r.data);
    setTiers(tr.data);
    setFees({ one_time_hire_fee: f.data.one_time_hire_fee, payroll_commission_pct: f.data.payroll_commission_pct });
    setIndustryRates(ir.data);
    setHouseRules({ content: hr.data.content, version: hr.data.version });
    setContracts(ct.data);
    setReports(rp.data);
    setPendingDocs(dc.data);
  };

  useEffect(() => { loadAll(); }, []);

  const saveTier = async () => {
    await api.post("/admin/tiers", editTier);
    toast.success("Tier saved");
    setEditTier(null);
    loadAll();
  };

  const deleteTier = async (id) => {
    await api.delete(`/admin/tiers/${id}`);
    toast.success("Tier archived");
    loadAll();
  };

  const saveFees = async () => {
    await api.put("/admin/fee-config", { one_time_hire_fee: Number(fees.one_time_hire_fee), payroll_commission_pct: Number(fees.payroll_commission_pct) });
    toast.success("Fee config updated");
  };

  const respondTicket = async (id, response, status = "in_progress") => {
    await api.patch(`/tickets/${id}`, { admin_response: response, status });
    toast.success("Ticket updated");
    loadAll();
  };

  const place = async () => {
    try {
      await api.post("/admin/place", { ...placeData, salary: Number(placeData.salary) });
      toast.success("Placement created");
      setPlaceOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const verifyEmp = async (emp) => {
    await api.patch(`/admin/employees/${emp.id}`, { verified: !emp.verified });
    toast.success(`Verified: ${!emp.verified}`);
    loadAll();
  };

  const saveIndustryRate = async () => {
    if (!editRate.category) return toast.error("Category required");
    await api.post("/admin/industry-rates", {
      category: editRate.category,
      hourly_min: Number(editRate.hourly_min), hourly_max: Number(editRate.hourly_max),
      daily_min: Number(editRate.daily_min), daily_max: Number(editRate.daily_max),
      monthly_min: Number(editRate.monthly_min), monthly_max: Number(editRate.monthly_max),
      note: editRate.note || "",
    });
    toast.success("Industry rate saved");
    setEditRate(null);
    loadAll();
  };

  const saveHouseRules = async () => {
    await api.put("/admin/house-rules", { content: houseRules.content, version: houseRules.version });
    toast.success("House Rules updated");
    loadAll();
  };

  const createContract = async () => {
    if (!contractForm.customer_id || !contractForm.employee_id || !contractForm.role_title || !contractForm.start_date) {
      return toast.error("Fill employer, staff, role and start date");
    }
    try {
      await api.post("/contracts", {
        ...contractForm,
        salary_agreed: Number(contractForm.salary_agreed),
        notice_period_days: Number(contractForm.notice_period_days),
        probation_days: Number(contractForm.probation_days),
      });
      toast.success("Contract drafted. Both parties will be notified to sign.");
      setContractOpen(false);
      setContractForm({
        customer_id: "", employee_id: "", role_title: "", salary_agreed: 0,
        pay_frequency: "monthly", start_date: "", notice_period_days: 14,
        probation_days: 30, additional_terms: "", working_hours: "8:00 AM – 5:00 PM, Mon–Sat",
      });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateReport = async () => {
    await api.patch(`/admin/reports/${reportEdit.id}`, {
      status: reportEdit.status,
      admin_notes: reportEdit.admin_notes,
      resolution: reportEdit.resolution,
    });
    toast.success("Report updated");
    setReportEdit(null);
    loadAll();
  };

  const reviewDocument = async (status) => {
    if (!docReview) return;
    await api.patch(`/admin/documents/${docReview.id}`, {
      status,
      admin_notes: docReview.admin_notes || "",
    });
    toast.success(`Document ${status}`);
    setDocReview(null);
    loadAll();
  };

  const docViewUrl = (d) => {
    const token = localStorage.getItem("ksb_token") || localStorage.getItem("esb_token");
    return `${api.defaults.baseURL}/documents/${d.id}/download?auth=${token}`;
  };

  const openProspectDocs = async (emp) => {
    try {
      const { data } = await api.get(`/admin/documents/by-employee/${emp.id}`);
      setProspectDocs({ open: true, employee: emp, docs: data });
    } catch (e) { toast.error("Failed to load documents"); }
  };

  const submitDocRequest = async () => {
    if (!requestDocs.employee_id || requestDocs.requested_kinds.length === 0) {
      return toast.error("Pick at least one document type");
    }
    try {
      await api.post("/admin/request-documents", {
        employee_id: requestDocs.employee_id,
        requested_kinds: requestDocs.requested_kinds,
        note: requestDocs.note,
      });
      toast.success("Request sent to staff member");
      setRequestDocs({ open: false, employee_id: null, requested_kinds: [], note: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveContractEdit = async () => {
    if (!contractEdit) return;
    try {
      const payload = {
        role_title: contractEdit.role_title,
        salary_agreed: Number(contractEdit.salary_agreed),
        pay_frequency: contractEdit.pay_frequency,
        start_date: contractEdit.start_date,
        end_date: contractEdit.end_date,
        working_hours: contractEdit.working_hours,
        notice_period_days: Number(contractEdit.notice_period_days),
        probation_days: Number(contractEdit.probation_days),
        additional_terms: contractEdit.additional_terms,
        status: contractEdit.status,
      };
      await api.patch(`/contracts/${contractEdit.id}`, payload);
      toast.success("Contract updated");
      setContractEdit(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const REQUESTABLE_DOCS = [
    { k: "national_id", label: "National ID" },
    { k: "kra_pin", label: "KRA PIN" },
    { k: "good_conduct", label: "Good Conduct" },
    { k: "cv_resume", label: "CV / Résumé" },
    { k: "certificate", label: "Training Certificate" },
    { k: "reference_letter", label: "Reference Letter" },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="label-eyebrow mb-2">Admin · Control Room</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">KSB Operations</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat label="Employers" value={stats.customers || 0} icon={Users} />
          <Stat label="Staff" value={stats.employees || 0} icon={Briefcase} />
          <Stat label="Placed" value={stats.placed || 0} icon={UserCheck} />
          <Stat label="Revenue" value={formatKES(stats.revenue_kes || 0)} icon={TrendingUp} />
        </div>

        <Tabs defaultValue="customers" className="mt-10">
          <TabsList className="tabs-3d-list">
            <TabsTrigger value="customers" data-testid="admin-tab-customers" className="tab-3d">Employers</TabsTrigger>
            <TabsTrigger value="prospects" data-testid="admin-tab-prospects" className="tab-3d">Prospects</TabsTrigger>
            <TabsTrigger value="placed" data-testid="admin-tab-placed" className="tab-3d">Placed</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="admin-tab-contracts" className="tab-3d">Contracts</TabsTrigger>
            <TabsTrigger value="verifications" data-testid="admin-tab-verifications" className="tab-3d">Verify</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports" className="tab-3d">Reports</TabsTrigger>
            <TabsTrigger value="rates" data-testid="admin-tab-rates" className="tab-3d">Rates</TabsTrigger>
            <TabsTrigger value="tiers" data-testid="admin-tab-tiers" className="tab-3d">Tiers</TabsTrigger>
            <TabsTrigger value="rules" data-testid="admin-tab-rules" className="tab-3d">Rules</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="admin-tab-tickets" className="tab-3d">Tickets</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="tab-3d">Reviews</TabsTrigger>
          </TabsList>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Phone</th>
                    <th className="px-4 py-3 font-semibold">Tier</th>
                    <th className="px-4 py-3 font-semibold">Subscription</th>
                    <th className="px-4 py-3 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-t border-[#E4E7F5]" data-testid={`cust-row-${c.id}`}>
                      <td className="px-4 py-3 font-semibold">{c.full_name}</td>
                      <td className="px-4 py-3">{c.email}</td>
                      <td className="px-4 py-3">{c.phone}</td>
                      <td className="px-4 py-3 capitalize"><Badge variant="outline">{c.tier}</Badge></td>
                      <td className="px-4 py-3">{c.subscription_status === "active" ? <Badge className="bg-[#1E3A8A]">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</td>
                      <td className="px-4 py-3">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PROSPECTS */}
          <TabsContent value="prospects" className="mt-6">
            <div className="flex justify-between mb-4">
              <div className="text-sm text-[#5B6478]">{employees.filter((e) => e.status === "prospect").length} prospects available for placement</div>
              <Button data-testid="manual-place-btn" onClick={() => setPlaceOpen(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
                <Plus className="w-4 h-4 mr-2" /> Manual placement
              </Button>
            </div>
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Location</th>
                    <th className="px-4 py-3 font-semibold">Tier</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Rating</th>
                    <th className="px-4 py-3 font-semibold">Documents</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => {
                    const empDocs = pendingDocs.filter((d) => d.employee_id === e.id);
                    const approvedCount = empDocs.filter((d) => d.status === "approved").length;
                    const pendingCount = empDocs.filter((d) => d.status === "pending").length;
                    return (
                    <tr key={e.id} className="border-t border-[#E4E7F5]">
                      <td className="px-4 py-3 font-semibold">{e.full_name}</td>
                      <td className="px-4 py-3 capitalize">{e.category.replace("-", " ")}</td>
                      <td className="px-4 py-3">{e.location}</td>
                      <td className="px-4 py-3 capitalize"><Badge variant="outline">{e.tier}</Badge></td>
                      <td className="px-4 py-3"><Badge className={e.status === "placed" ? "bg-[#1E3A8A]" : ""} variant={e.status === "placed" ? "default" : "outline"}>{e.status}</Badge></td>
                      <td className="px-4 py-3">{e.rating_avg?.toFixed(1) || "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`view-docs-${e.id}`}
                          onClick={() => openProspectDocs(e)}
                          className="text-xs font-semibold text-[#1E3A8A] hover:text-[#7C3AED] flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          {empDocs.length}
                          {pendingCount > 0 && <span className="text-amber-600">· {pendingCount} pending</span>}
                          {approvedCount > 0 && <span className="text-[#1E3A8A]">· {approvedCount} ✓</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button data-testid={`verify-${e.id}`} size="sm" variant="outline" className="border-[#E4E7F5]" onClick={() => verifyEmp(e)}>
                            {e.verified ? "Unverify" : "Verify"}
                          </Button>
                          <Button data-testid={`request-docs-${e.id}`} size="sm" variant="outline" className="border-[#7C3AED] text-[#7C3AED]" onClick={() => setRequestDocs({ open: true, employee_id: e.id, employee_name: e.full_name, requested_kinds: [], note: "" })}>
                            <MessageSquare className="w-3 h-3 mr-1" /> Request
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PLACED */}
          <TabsContent value="placed" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Staff</th>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold">Employer</th>
                    <th className="px-4 py-3 font-semibold">Salary</th>
                    <th className="px-4 py-3 font-semibold">Start</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {placements.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5B6478]">No placements yet</td></tr>}
                  {placements.map((p) => (
                    <tr key={p.id} className="border-t border-[#E4E7F5]">
                      <td className="px-4 py-3 font-semibold">{p.employee_name}</td>
                      <td className="px-4 py-3 capitalize">{p.role || p.category?.replace("-", " ")}</td>
                      <td className="px-4 py-3">{p.customer_name}</td>
                      <td className="px-4 py-3">{formatKES(p.salary)}</td>
                      <td className="px-4 py-3">{p.start_date}</td>
                      <td className="px-4 py-3">{p.active ? <Badge className="bg-[#1E3A8A]">Active</Badge> : <Badge variant="outline">Ended</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CONTRACTS */}
          <TabsContent value="contracts" className="mt-6">
            <div className="flex justify-between mb-4">
              <div className="text-sm text-[#5B6478]">{contracts.length} contracts</div>
              <Button data-testid="new-contract-btn" onClick={() => setContractOpen(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
                <Plus className="w-4 h-4 mr-2" /> Draft new contract
              </Button>
            </div>
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold">Staff</th>
                    <th className="px-4 py-3 font-semibold">Employer</th>
                    <th className="px-4 py-3 font-semibold">Salary</th>
                    <th className="px-4 py-3 font-semibold">Signatures</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#5B6478]">No contracts yet</td></tr>}
                  {contracts.map((c) => (
                    <tr key={c.id} className="border-t border-[#E4E7F5]" data-testid={`admin-contract-${c.id}`}>
                      <td className="px-4 py-3 font-semibold">{c.role_title}</td>
                      <td className="px-4 py-3">{c.employee_name}</td>
                      <td className="px-4 py-3">{c.customer_name}</td>
                      <td className="px-4 py-3">{formatKES(c.salary_agreed)}/{c.pay_frequency}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 text-xs">
                          <span className={c.customer_signed ? "text-[#1E3A8A]" : "text-amber-600"}>Employer: {c.customer_signed ? "✓" : "⧗"}</span>
                          <span className={c.employee_signed ? "text-[#1E3A8A]" : "text-amber-600"}>Staff: {c.employee_signed ? "✓" : "⧗"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Badge className={`capitalize ${c.status === "active" ? "bg-[#1E3A8A]" : c.status === "terminated" ? "bg-[#B91C1C]" : "bg-amber-100 text-amber-900 hover:bg-amber-100"}`}>{c.status?.replace("_", " ")}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" data-testid={`view-contract-${c.id}`} className="border-[#E4E7F5]" onClick={() => window.open(`/contracts/${c.id}`, '_blank')}>
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`edit-contract-${c.id}`} className="border-[#7C3AED] text-[#7C3AED]" onClick={() => setContractEdit({ ...c })}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="verifications" className="mt-6">
            <div className="mb-4 flex justify-between items-end">
              <div>
                <h2 className="font-display text-2xl font-bold">Document Verifications</h2>
                <p className="text-sm text-[#5B6478] mt-1">Review staff-uploaded IDs, certificates and references. Approving key documents auto-verifies the staff member.</p>
              </div>
              <div className="text-sm text-[#5B6478]">
                {pendingDocs.filter((d) => d.status === "pending").length} pending
              </div>
            </div>
            <div className="space-y-3">
              {pendingDocs.length === 0 && <div className="text-center py-10 text-[#5B6478]">No documents submitted yet</div>}
              {pendingDocs.map((d) => (
                <div key={d.id} className="bg-white border border-[#E4E7F5] rounded-md p-5 flex items-start justify-between gap-3" data-testid={`admin-doc-${d.id}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <FileText className="w-8 h-8 text-[#1E3A8A] shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-display font-semibold">{d.title || d.original_filename}</div>
                      <div className="text-xs text-[#5B6478] mt-1 capitalize">
                        {d.kind.replace("_", " ")} · from <span className="font-semibold">{d.employee_name}</span> · uploaded {new Date(d.created_at).toLocaleDateString()}
                      </div>
                      {d.admin_notes && (
                        <div className="text-xs text-[#5B6478] mt-2 bg-[#EEF0FA] rounded p-2">
                          <span className="label-eyebrow">Note:</span> {d.admin_notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`capitalize ${d.status === "approved" ? "bg-[#1E3A8A]" : d.status === "rejected" ? "bg-[#B91C1C]" : "bg-amber-100 text-amber-900 hover:bg-amber-100"}`}>
                      {d.status}
                    </Badge>
                    <a href={docViewUrl(d)} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" data-testid={`admin-doc-view-${d.id}`} className="border-[#E4E7F5]">
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                    </a>
                    <Button
                      size="sm"
                      data-testid={`admin-doc-review-${d.id}`}
                      onClick={() => setDocReview({ ...d })}
                      className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
                    >
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-6 space-y-3">
            {reports.length === 0 && <div className="text-center py-10 text-[#5B6478]">No reports</div>}
            {reports.map((r) => (
              <div key={r.id} className="bg-white border border-[#E4E7F5] rounded-md p-5" data-testid={`admin-report-${r.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#EEF0FA] text-[#0F1E4B] hover:bg-[#EEF0FA] capitalize">{r.kind.replace("_", " ")}</Badge>
                      <span className="font-display font-semibold">{r.subject}</span>
                    </div>
                    <p className="text-sm text-[#5B6478] mt-2">{r.description}</p>
                    <div className="text-xs text-[#5B6478] mt-2">By {r.reporter_name} ({r.reporter_role}) · {new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`capitalize ${r.status === "resolved" ? "bg-[#1E3A8A]" : r.status === "in_progress" ? "bg-amber-100 text-amber-900 hover:bg-amber-100" : "bg-[#B91C1C]"}`}>
                      {r.status?.replace("_", " ")}
                    </Badge>
                    <Button size="sm" variant="outline" data-testid={`manage-report-${r.id}`} className="border-[#1E3A8A] text-[#1E3A8A]" onClick={() => setReportEdit({ ...r })}>Manage</Button>
                  </div>
                </div>
                {r.resolution && (
                  <div className="mt-3 bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 rounded-md p-3 text-sm">
                    <span className="label-eyebrow">Resolution:</span> {r.resolution}
                  </div>
                )}
                <InlineChat
                  messages={r.messages || []}
                  currentUserId={currentUser?.id}
                  locked={r.status === "resolved" || r.status === "dismissed"}
                  testIdPrefix={`admin-report-chat-${r.id}`}
                  onSend={async (body) => {
                    try {
                      await api.post(`/reports/${r.id}/messages`, { body });
                      loadAll();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                />
              </div>
            ))}
          </TabsContent>

          {/* INDUSTRY RATES */}
          <TabsContent value="rates" className="mt-6">
            <div className="mb-4 flex justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold">Industry Rate Bands</h2>
                <p className="text-sm text-[#5B6478] mt-1">These bands are shown publicly to employers. The bureau sets each staff member's actual pay within (or above) these bands.</p>
              </div>
            </div>
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Hourly</th>
                    <th className="px-4 py-3 font-semibold">Daily</th>
                    <th className="px-4 py-3 font-semibold">Monthly</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {industryRates.map((ir) => (
                    <tr key={ir.category} className="border-t border-[#E4E7F5]">
                      <td className="px-4 py-3 font-semibold capitalize">{ir.category.replace("-", " ")}</td>
                      <td className="px-4 py-3">{formatKES(ir.hourly_min)} – {formatKES(ir.hourly_max)}</td>
                      <td className="px-4 py-3">{formatKES(ir.daily_min)} – {formatKES(ir.daily_max)}</td>
                      <td className="px-4 py-3">{formatKES(ir.monthly_min)} – {formatKES(ir.monthly_max)}</td>
                      <td className="px-4 py-3">
                        <Button data-testid={`edit-rate-${ir.category}`} size="sm" variant="outline" className="border-[#E4E7F5]" onClick={() => setEditRate({ ...ir })}>
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* HOUSE RULES */}
          <TabsContent value="rules" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md p-6">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold flex items-center gap-2"><BookOpen className="w-5 h-5" /> House Rules Editor</h2>
                  <p className="text-sm text-[#5B6478] mt-1">Every new staff member must accept this document before onboarding. Bumping the version invalidates previous acknowledgements.</p>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <Label className="label-eyebrow">Version</Label>
                    <Input data-testid="rules-version" value={houseRules.version} onChange={(e) => setHouseRules({ ...houseRules, version: e.target.value })} className="mt-2 w-32 border-[#E4E7F5]" />
                  </div>
                  <Button data-testid="save-rules" onClick={saveHouseRules} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              </div>
              <Textarea
                data-testid="rules-content"
                value={houseRules.content}
                onChange={(e) => setHouseRules({ ...houseRules, content: e.target.value })}
                rows={22}
                className="border-[#E4E7F5] font-mono text-sm"
              />
            </div>
          </TabsContent>

          {/* TIERS & FEES */}
          <TabsContent value="tiers" className="mt-6 space-y-8">
            <div>
              <div className="flex justify-between mb-4">
                <h2 className="font-display text-2xl font-bold">Subscription Tiers</h2>
                <Button data-testid="new-tier-btn" onClick={() => setEditTier({ name: "", audience: "customer", price: 0, period: "monthly", features: [], active: true })} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">
                  <Plus className="w-4 h-4 mr-2" /> New tier
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tiers.map((t) => (
                  <div key={t.id} className="bg-white border border-[#E4E7F5] rounded-md p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-display font-bold text-lg">{t.name}</div>
                        <div className="text-xs text-[#5B6478] capitalize mt-0.5">{t.audience} · {t.period}</div>
                      </div>
                      <div className="font-display font-extrabold text-2xl text-[#1E3A8A]">{formatKES(t.price)}</div>
                    </div>
                    <ul className="mt-3 text-sm space-y-1 text-[#5B6478]">
                      {t.features.map((f, i) => <li key={i}>• {f}</li>)}
                    </ul>
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`edit-tier-${t.id}`} className="border-[#E4E7F5]" onClick={() => setEditTier(t)}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`del-tier-${t.id}`} className="border-[#B91C1C] text-[#B91C1C]" onClick={() => deleteTier(t.id)}>
                        <Trash2 className="w-3 h-3 mr-1" /> Archive
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E4E7F5] rounded-md p-6 max-w-2xl">
              <h2 className="font-display text-2xl font-bold mb-4">Platform Fees</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">One-time hire fee (KES)</Label>
                  <Input data-testid="fee-onetime" type="number" value={fees.one_time_hire_fee} onChange={(e) => setFees({ ...fees, one_time_hire_fee: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Payroll commission (%)</Label>
                  <Input data-testid="fee-commission" type="number" value={fees.payroll_commission_pct} onChange={(e) => setFees({ ...fees, payroll_commission_pct: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
              </div>
              <Button data-testid="save-fees" onClick={saveFees} className="mt-4 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Save fees</Button>
            </div>
          </TabsContent>

          {/* TICKETS */}
          <TabsContent value="tickets" className="mt-6 space-y-3">
            {tickets.length === 0 && <div className="text-center py-10 text-[#5B6478]">No tickets yet</div>}
            {tickets.map((t) => (
              <div key={t.id} className="bg-white border border-[#E4E7F5] rounded-md p-5" data-testid={`ticket-${t.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-display font-semibold">{t.subject}</div>
                    <div className="text-xs text-[#5B6478]">From: {t.customer_name}</div>
                    <div className="text-sm mt-2">{t.message}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="capitalize" variant="outline">{t.status?.replace("_", " ")}</Badge>
                    {t.status !== "resolved" && (
                      <Button data-testid={`resolve-${t.id}`} size="sm" variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]" onClick={async () => { await api.patch(`/tickets/${t.id}`, { status: "resolved" }); loadAll(); }}>
                        Mark resolved
                      </Button>
                    )}
                  </div>
                </div>
                <InlineChat
                  messages={t.messages || []}
                  currentUserId={currentUser?.id}
                  locked={t.status === "resolved"}
                  testIdPrefix={`admin-ticket-chat-${t.id}`}
                  onSend={async (body) => {
                    try {
                      await api.post(`/tickets/${t.id}/messages`, { body });
                      loadAll();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                />
              </div>
            ))}
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-6 space-y-3">
            {reviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E4E7F5] rounded-md p-5 flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold">{r.customer_name}</span>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#7C3AED] text-[#7C3AED]" : "text-[#E4E7F5]"}`} />)}
                    </div>
                  </div>
                  <p className="text-sm text-[#5B6478] mt-2">{r.comment}</p>
                </div>
                <Button data-testid={`del-review-${r.id}`} variant="outline" size="sm" className="border-[#B91C1C] text-[#B91C1C]" onClick={async () => { await api.delete(`/admin/reviews/${r.id}`); loadAll(); }}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* TIER EDIT */}
      <Dialog open={!!editTier} onOpenChange={(o) => !o && setEditTier(null)}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editTier?.id ? "Edit tier" : "New tier"}</DialogTitle>
          </DialogHeader>
          {editTier && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Name</Label>
                  <Input data-testid="tier-name" value={editTier.name} onChange={(e) => setEditTier({ ...editTier, name: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Audience</Label>
                  <Select value={editTier.audience} onValueChange={(v) => setEditTier({ ...editTier, audience: v })}>
                    <SelectTrigger data-testid="tier-audience" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">Price (KES)</Label>
                  <Input data-testid="tier-price" type="number" value={editTier.price} onChange={(e) => setEditTier({ ...editTier, price: Number(e.target.value) })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Period</Label>
                  <Select value={editTier.period} onValueChange={(v) => setEditTier({ ...editTier, period: v })}>
                    <SelectTrigger data-testid="tier-period" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="one_time">One time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Features (one per line)</Label>
                <Textarea data-testid="tier-features" rows={4} value={editTier.features.join("\n")} onChange={(e) => setEditTier({ ...editTier, features: e.target.value.split("\n").filter(Boolean) })} className="mt-2 border-[#E4E7F5]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTier(null)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="save-tier" onClick={saveTier} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MANUAL PLACE */}
      <Dialog open={placeOpen} onOpenChange={setPlaceOpen}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader><DialogTitle className="font-display text-2xl">Manual placement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow">Customer</Label>
              <Select value={placeData.customer_id} onValueChange={(v) => setPlaceData({ ...placeData, customer_id: v })}>
                <SelectTrigger data-testid="place-customer" className="mt-2 border-[#E4E7F5]"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Staff</Label>
              <Select value={placeData.employee_id} onValueChange={(v) => setPlaceData({ ...placeData, employee_id: v })}>
                <SelectTrigger data-testid="place-worker" className="mt-2 border-[#E4E7F5]"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
                <SelectContent>
                  {employees.filter((e) => e.status === "prospect").map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.category}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Role" data-testid="place-role" value={placeData.role} onChange={(e) => setPlaceData({ ...placeData, role: e.target.value })} className="border-[#E4E7F5]" />
              <Input placeholder="Salary" data-testid="place-salary" type="number" value={placeData.salary} onChange={(e) => setPlaceData({ ...placeData, salary: e.target.value })} className="border-[#E4E7F5]" />
              <Input type="date" data-testid="place-date" value={placeData.start_date} onChange={(e) => setPlaceData({ ...placeData, start_date: e.target.value })} className="border-[#E4E7F5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlaceOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="place-submit" onClick={place} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Place</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* INDUSTRY RATE EDIT */}
      <Dialog open={!!editRate} onOpenChange={(o) => !o && setEditRate(null)}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit rate: <span className="capitalize">{editRate?.category?.replace("-", " ")}</span></DialogTitle>
            <DialogDescription>Values are in KES. These are shown to customers as ranges.</DialogDescription>
          </DialogHeader>
          {editRate && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-eyebrow">Hourly min</Label>
                <Input data-testid="rate-hmin" type="number" value={editRate.hourly_min} onChange={(e) => setEditRate({ ...editRate, hourly_min: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Hourly max</Label>
                <Input data-testid="rate-hmax" type="number" value={editRate.hourly_max} onChange={(e) => setEditRate({ ...editRate, hourly_max: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Daily min</Label>
                <Input data-testid="rate-dmin" type="number" value={editRate.daily_min} onChange={(e) => setEditRate({ ...editRate, daily_min: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Daily max</Label>
                <Input data-testid="rate-dmax" type="number" value={editRate.daily_max} onChange={(e) => setEditRate({ ...editRate, daily_max: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Monthly min</Label>
                <Input data-testid="rate-mmin" type="number" value={editRate.monthly_min} onChange={(e) => setEditRate({ ...editRate, monthly_min: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Monthly max</Label>
                <Input data-testid="rate-mmax" type="number" value={editRate.monthly_max} onChange={(e) => setEditRate({ ...editRate, monthly_max: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRate(null)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="save-rate" onClick={saveIndustryRate} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONTRACT DRAFT */}
      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent className="bg-[#F8F9FE] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Draft contract</DialogTitle>
            <DialogDescription>KSB drafts the contract on behalf of both parties. Both must then sign electronically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-eyebrow">Customer / Employer</Label>
                <Select value={contractForm.customer_id} onValueChange={(v) => setContractForm({ ...contractForm, customer_id: v })}>
                  <SelectTrigger data-testid="contract-customer" className="mt-2 border-[#E4E7F5]"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Staff</Label>
                <Select value={contractForm.employee_id} onValueChange={(v) => setContractForm({ ...contractForm, employee_id: v })}>
                  <SelectTrigger data-testid="contract-worker" className="mt-2 border-[#E4E7F5]"><SelectValue placeholder="Pick staff" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.category}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="label-eyebrow">Role title</Label>
                <Input data-testid="contract-role" value={contractForm.role_title} onChange={(e) => setContractForm({ ...contractForm, role_title: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="e.g. Live-in Nanny" />
              </div>
              <div>
                <Label className="label-eyebrow">Salary (KES)</Label>
                <Input data-testid="contract-salary" type="number" value={contractForm.salary_agreed} onChange={(e) => setContractForm({ ...contractForm, salary_agreed: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Pay frequency</Label>
                <Select value={contractForm.pay_frequency} onValueChange={(v) => setContractForm({ ...contractForm, pay_frequency: v })}>
                  <SelectTrigger data-testid="contract-freq" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Start date</Label>
                <Input data-testid="contract-start" type="date" value={contractForm.start_date} onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Working hours</Label>
                <Input data-testid="contract-hours" value={contractForm.working_hours} onChange={(e) => setContractForm({ ...contractForm, working_hours: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Notice period (days)</Label>
                <Input data-testid="contract-notice" type="number" value={contractForm.notice_period_days} onChange={(e) => setContractForm({ ...contractForm, notice_period_days: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Probation (days)</Label>
                <Input data-testid="contract-probation" type="number" value={contractForm.probation_days} onChange={(e) => setContractForm({ ...contractForm, probation_days: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div className="col-span-2">
                <Label className="label-eyebrow">Additional terms (optional)</Label>
                <Textarea data-testid="contract-additional" rows={3} value={contractForm.additional_terms} onChange={(e) => setContractForm({ ...contractForm, additional_terms: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="Job description, benefits, accommodation, etc." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="contract-submit" onClick={createContract} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Draft & send for signatures</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT MANAGE */}
      <Dialog open={!!reportEdit} onOpenChange={(o) => !o && setReportEdit(null)}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Manage report</DialogTitle>
          </DialogHeader>
          {reportEdit && (
            <div className="space-y-4">
              <div className="text-sm">
                <div><strong>Kind:</strong> {reportEdit.kind}</div>
                <div className="mt-1"><strong>Subject:</strong> {reportEdit.subject}</div>
                <div className="mt-1 text-[#5B6478]">{reportEdit.description}</div>
              </div>
              <div>
                <Label className="label-eyebrow">Status</Label>
                <Select value={reportEdit.status} onValueChange={(v) => setReportEdit({ ...reportEdit, status: v })}>
                  <SelectTrigger data-testid="report-status" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Internal notes</Label>
                <Textarea data-testid="report-notes" rows={3} value={reportEdit.admin_notes || ""} onChange={(e) => setReportEdit({ ...reportEdit, admin_notes: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Resolution shared with reporter</Label>
                <Textarea data-testid="report-resolution" rows={3} value={reportEdit.resolution || ""} onChange={(e) => setReportEdit({ ...reportEdit, resolution: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportEdit(null)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="report-save" onClick={updateReport} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* DOCUMENT REVIEW */}
      <Dialog open={!!docReview} onOpenChange={(o) => !o && setDocReview(null)}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Review document</DialogTitle>
            <DialogDescription>Verify authenticity, cross-check with the staff record, then approve or reject.</DialogDescription>
          </DialogHeader>
          {docReview && (
            <div className="space-y-4">
              <div className="text-sm bg-[#EEF0FA] rounded-md p-3">
                <div><strong>Staff:</strong> {docReview.employee_name}</div>
                <div className="mt-1 capitalize"><strong>Type:</strong> {docReview.kind.replace("_", " ")}</div>
                <div className="mt-1"><strong>Title:</strong> {docReview.title}</div>
                <div className="mt-1"><strong>File:</strong> {docReview.original_filename}</div>
              </div>
              <a href={docViewUrl(docReview)} target="_blank" rel="noreferrer" className="inline-block">
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]" data-testid="doc-review-open">
                  <Eye className="w-4 h-4 mr-2" /> Open document
                </Button>
              </a>
              <div>
                <Label className="label-eyebrow">Notes (shared with staff)</Label>
                <Textarea data-testid="doc-review-notes" rows={3} value={docReview.admin_notes || ""} onChange={(e) => setDocReview({ ...docReview, admin_notes: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="Why rejected, or extra info if approved" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="doc-review-reject" onClick={() => reviewDocument("rejected")} className="border-[#B91C1C] text-[#B91C1C]">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
            <Button data-testid="doc-review-approve" onClick={() => reviewDocument("approved")} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROSPECT DOCUMENTS */}
      <Dialog open={prospectDocs.open} onOpenChange={(o) => !o && setProspectDocs({ open: false, employee: null, docs: [] })}>
        <DialogContent className="bg-[#F8F9FE] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Documents · {prospectDocs.employee?.full_name}</DialogTitle>
            <DialogDescription>All documents uploaded by this staff member. Open in new tab for full review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {prospectDocs.docs.length === 0 && <div className="text-center py-8 text-[#5B6478]">No documents uploaded yet.</div>}
            {prospectDocs.docs.map((d) => (
              <div key={d.id} className="bg-white border border-[#E4E7F5] rounded-md p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-6 h-6 text-[#1E3A8A] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{d.title || d.original_filename}</div>
                    <div className="text-xs text-[#5B6478] capitalize">{d.kind.replace("_", " ")} · {Math.round((d.size || 0) / 1024)} KB · {d.content_type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={`capitalize ${d.status === "approved" ? "bg-[#1E3A8A]" : d.status === "rejected" ? "bg-[#B91C1C]" : "bg-amber-100 text-amber-900 hover:bg-amber-100"}`}>{d.status}</Badge>
                  <a href={docViewUrl(d)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" data-testid={`prospect-doc-view-${d.id}`} className="border-[#E4E7F5]">
                      <Eye className="w-3 h-3 mr-1" /> Open
                    </Button>
                  </a>
                  {d.status === "pending" && (
                    <Button
                      size="sm"
                      data-testid={`prospect-doc-review-${d.id}`}
                      onClick={() => { setDocReview({ ...d }); setProspectDocs({ open: false, employee: null, docs: [] }); }}
                      className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white"
                    >
                      Review
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* REQUEST DOCUMENTS */}
      <Dialog open={requestDocs.open} onOpenChange={(o) => !o && setRequestDocs({ open: false, employee_id: null, requested_kinds: [], note: "" })}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Request documents</DialogTitle>
            <DialogDescription>Tick every document you need from this staff member. They will see the request in their dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {REQUESTABLE_DOCS.map(({ k, label }) => {
                const checked = requestDocs.requested_kinds.includes(k);
                return (
                  <label key={k} className={`flex items-center gap-2 p-2.5 border rounded-md cursor-pointer text-sm ${checked ? "border-[#1E3A8A] bg-[#EEF0FA]" : "border-[#E4E7F5]"}`}>
                    <input
                      data-testid={`req-doc-${k}`}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const set = new Set(requestDocs.requested_kinds);
                        if (e.target.checked) set.add(k); else set.delete(k);
                        setRequestDocs({ ...requestDocs, requested_kinds: [...set] });
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <div>
              <Label className="label-eyebrow">Note to staff (optional)</Label>
              <Textarea data-testid="req-doc-note" rows={3} value={requestDocs.note} onChange={(e) => setRequestDocs({ ...requestDocs, note: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="e.g. Please upload clear scans of both sides of your ID." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDocs({ open: false, employee_id: null, requested_kinds: [], note: "" })} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="req-doc-submit" onClick={submitDocRequest} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONTRACT EDIT (admin) */}
      <Dialog open={!!contractEdit} onOpenChange={(o) => !o && setContractEdit(null)}>
        <DialogContent className="bg-[#F8F9FE] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit contract</DialogTitle>
            <DialogDescription>Admin overrides: end placement, extend period, change hours, adjust salary. Signed contracts remain historic; edits are logged with a timestamp.</DialogDescription>
          </DialogHeader>
          {contractEdit && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="label-eyebrow">Role title</Label>
                <Input data-testid="cedit-role" value={contractEdit.role_title || ""} onChange={(e) => setContractEdit({ ...contractEdit, role_title: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Salary (KES)</Label>
                <Input data-testid="cedit-salary" type="number" value={contractEdit.salary_agreed || 0} onChange={(e) => setContractEdit({ ...contractEdit, salary_agreed: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Pay frequency</Label>
                <Select value={contractEdit.pay_frequency} onValueChange={(v) => setContractEdit({ ...contractEdit, pay_frequency: v })}>
                  <SelectTrigger data-testid="cedit-freq" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Start date</Label>
                <Input data-testid="cedit-start" type="date" value={contractEdit.start_date || ""} onChange={(e) => setContractEdit({ ...contractEdit, start_date: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">End date (extend/set)</Label>
                <Input data-testid="cedit-end" type="date" value={contractEdit.end_date || ""} onChange={(e) => setContractEdit({ ...contractEdit, end_date: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div className="col-span-2">
                <Label className="label-eyebrow">Working hours</Label>
                <Input data-testid="cedit-hours" value={contractEdit.working_hours || ""} onChange={(e) => setContractEdit({ ...contractEdit, working_hours: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Notice period (days)</Label>
                <Input data-testid="cedit-notice" type="number" value={contractEdit.notice_period_days || 0} onChange={(e) => setContractEdit({ ...contractEdit, notice_period_days: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Probation (days)</Label>
                <Input data-testid="cedit-probation" type="number" value={contractEdit.probation_days || 0} onChange={(e) => setContractEdit({ ...contractEdit, probation_days: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div className="col-span-2">
                <Label className="label-eyebrow">Additional terms</Label>
                <Textarea data-testid="cedit-terms" rows={3} value={contractEdit.additional_terms || ""} onChange={(e) => setContractEdit({ ...contractEdit, additional_terms: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div className="col-span-2">
                <Label className="label-eyebrow">Status</Label>
                <Select value={contractEdit.status} onValueChange={(v) => setContractEdit({ ...contractEdit, status: v })}>
                  <SelectTrigger data-testid="cedit-status" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_signatures">Pending signatures</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="terminated">Terminated (end placement)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#5B6478] mt-2">Setting status to Terminated will end the placement and return the staff member to the prospect pool.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractEdit(null)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="cedit-save" onClick={saveContractEdit} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E4E7F5] rounded-md p-5 flex items-center justify-between">
      <div>
        <div className="label-eyebrow">{label}</div>
        <div className="font-display text-2xl font-bold mt-1">{value}</div>
      </div>
      <Icon className="w-6 h-6 text-[#1E3A8A]" />
    </div>
  );
}
